import { Build, Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method, Prop, Watch } from '@stencil/core';
import Swiper, { Autoplay, Navigation, Pagination, Scrollbar, SwiperOptions } from 'swiper/.';
import { componentOnReady } from '../../utils/helpers';
import CustomEffectFade from './custom-effect-fade';

@Component({
  tag: 'vds-slides',
  styleUrl: 'vds-slides.scss',
  shadow: true,
})
export class VdsSlides implements ComponentInterface {
  private scrollbarEl?: HTMLElement;
  private pagerEl?: HTMLElement;
  private swiperReady = false;
  private mutationO?: MutationObserver;
  private readySwiper!: (swiper: Swiper) => void;
  private swiper: Promise<Swiper> = new Promise((resolve) => {
    this.readySwiper = resolve;
  });

  @Element() el!: HTMLElement;

  /**
   * Options to pass to the swiper instance.
   * See https://swiperjs.com/swiper-api for valid options
   */
  @Prop() options: any = {}; // SwiperOptions;  // TODO

  /**
   * Transition effect. Can be `'slide'`, `'fade'`
   */
  @Prop() effect?: 'slide' | 'fade';

  /**
   * Enables the Slider autoplay functionality
   */
  @Prop() autoplay: boolean = false;

  /**
   * Autoplay Delay in ms
   */
  @Prop() delay: number = 3000;

  @Watch('options')
  async optionsChanged() {
    if (this.swiperReady) {
      const swiper = await this.getSwiper();
      if (swiper?.params) {
        Object.assign(swiper.params, this.options);
        await this.update();
      }
    }
  }

  /**
   * If `true`, show the pagination.
   */
  @Prop() pager = false;

  /**
   * If `true`, show the scrollbar.
   */
  @Prop() scrollbar = false;

  /**
   * If `true`, show the navigation arrows
   */
  @Prop() navigation: boolean = false;

  /**
   * Emitted after Swiper initialization
   */
  @Event() vdsSlidesDidLoad!: EventEmitter<void>;

  /**
   * Emitted when the user taps/clicks on the slide's container.
   */
  @Event() vdsSlideTap!: EventEmitter<void>;

  /**
   * Emitted when the user double taps on the slide's container.
   */
  @Event() vdsSlideDoubleTap!: EventEmitter<void>;

  /**
   * Emitted before the active slide has changed.
   */
  @Event() vdsSlideWillChange!: EventEmitter<void>;

  /**
   * Emitted after the active slide has changed.
   */
  @Event() vdsSlideDidChange!: EventEmitter<void>;

  /**
   * Emitted when the next slide has started.
   */
  @Event() vdsSlideNextStart!: EventEmitter<void>;

  /**
   * Emitted when the previous slide has started.
   */
  @Event() vdsSlidePrevStart!: EventEmitter<void>;

  /**
   * Emitted when the next slide has ended.
   */
  @Event() vdsSlideNextEnd!: EventEmitter<void>;

  /**
   * Emitted when the previous slide has ended.
   */
  @Event() vdsSlidePrevEnd!: EventEmitter<void>;

  /**
   * Emitted when the slide transition has started.
   */
  @Event() vdsSlideTransitionStart!: EventEmitter<void>;

  /**
   * Emitted when the slide transition has ended.
   */
  @Event() vdsSlideTransitionEnd!: EventEmitter<void>;

  /**
   * Emitted when the slider is actively being moved.
   */
  @Event() vdsSlideDrag!: EventEmitter<void>;

  /**
   * Emitted when the slider is at its initial position.
   */
  @Event() vdsSlideReachStart!: EventEmitter<void>;

  /**
   * Emitted when the slider is at the last slide.
   */
  @Event() vdsSlideReachEnd!: EventEmitter<void>;

  /**
   * Emitted when the user first touches the slider.
   */
  @Event() vdsSlideTouchStart!: EventEmitter<void>;

  /**
   * Emitted when the user releases the touch.
   */
  @Event() vdsSlideTouchEnd!: EventEmitter<void>;

  connectedCallback() {
    if (Build.isBrowser) {
      const mut = (this.mutationO = new MutationObserver(() => {
        if (this.swiperReady) {
          this.update();
        }
      }));
      mut.observe(this.el, {
        childList: true,
        subtree: true,
      });

      componentOnReady(this.el, () => {
        this.initSwiper();
      });
    }
  }

  disconnectedCallback() {
    if (this.mutationO) {
      this.mutationO.disconnect();
      this.mutationO = undefined;
    }
  }

  /**
   * Update the underlying slider implementation. Call this if you've added or removed
   * child slides.
   */
  @Method()
  async update() {
    const [swiper] = await Promise.all([this.getSwiper(), waitForSlides(this.el)]);
    swiper.update();
  }

  /**
   * Force swiper to update its height (when autoHeight is enabled) for the duration
   * equal to 'speed' parameter.
   *
   * @param speed The transition duration (in ms).
   */
  @Method()
  async updateAutoHeight(speed?: number) {
    const swiper = await this.getSwiper();
    swiper.updateAutoHeight(speed);
  }

  /**
   * Transition to the specified slide.
   *
   * @param index The index of the slide to transition to.
   * @param speed The transition duration (in ms).
   * @param runCallbacks If true, the transition will produce [Transition/SlideChange][Start/End] transition events.
   */
  @Method()
  async slideTo(index: number, speed?: number, runCallbacks?: boolean) {
    const swiper = await this.getSwiper();
    swiper.slideTo(index, speed, runCallbacks);
  }

  /**
   * Transition to the next slide.
   *
   * @param speed The transition duration (in ms).
   * @param runCallbacks If true, the transition will produce [Transition/SlideChange][Start/End] transition events.
   */
  @Method()
  async slideNext(speed?: number, runCallbacks?: boolean) {
    const swiper = await this.getSwiper();
    swiper.slideNext(speed!, runCallbacks!);
  }

  /**
   * Transition to the previous slide.
   *
   * @param speed The transition duration (in ms).
   * @param runCallbacks If true, the transition will produce the [Transition/SlideChange][Start/End] transition events.
   */
  @Method()
  async slidePrev(speed?: number, runCallbacks?: boolean) {
    const swiper = await this.getSwiper();
    swiper.slidePrev(speed, runCallbacks);
  }

  /**
   * Get the index of the active slide.
   */
  @Method()
  async getActiveIndex(): Promise<number> {
    const swiper = await this.getSwiper();
    return swiper.activeIndex;
  }

  /**
   * Get the index of the previous slide.
   */
  @Method()
  async getPreviousIndex(): Promise<number> {
    const swiper = await this.getSwiper();
    return swiper.previousIndex;
  }

  /**
   * Get the total number of slides.
   */
  @Method()
  async length(): Promise<number> {
    const swiper = await this.getSwiper();
    return swiper.slides.length;
  }

  /**
   * Get whether or not the current slide is the last slide.
   */
  @Method()
  async isEnd(): Promise<boolean> {
    const swiper = await this.getSwiper();
    return swiper.isEnd;
  }

  /**
   * Get whether or not the current slide is the first slide.
   */
  @Method()
  async isBeginning(): Promise<boolean> {
    const swiper = await this.getSwiper();
    return swiper.isBeginning;
  }

  /**
   * Start auto play.
   */
  @Method()
  async startAutoplay() {
    const swiper = await this.getSwiper();
    if (swiper.autoplay) {
      swiper.autoplay.start();
    }
  }

  /**
   * Stop auto play.
   */
  @Method()
  async stopAutoplay() {
    const swiper = await this.getSwiper();
    if (swiper.autoplay) {
      swiper.autoplay.stop();
    }
  }

  /**
   * Sets number of slides per view
   */
  @Method()
  async setSlidesPerView(value: number) {
    const swiper = await this.getSwiper();
    swiper.params.slidesPerView = value;
  }

  /**
   * Sets space between slides (in px)
   */
  @Method()
  async setSpaceBetween(value: number) {
    const swiper = await this.getSwiper();
    swiper.params.spaceBetween = value;
  }

  /**
   * Disables centering of slides
   */
  @Method()
  async disableCentering() {
    const swiper = await this.getSwiper();
    swiper.params.centeredSlides = false;
  }

  /**
   * Lock or unlock the ability to slide to the next slide.
   *
   * @param lock If `true`, disable swiping to the next slide.
   */
  @Method()
  async lockSwipeToNext(lock: boolean) {
    const swiper = await this.getSwiper();
    swiper.allowSlideNext = !lock;
  }

  /**
   * Lock or unlock the ability to slide to the previous slide.
   *
   * @param lock If `true`, disable swiping to the previous slide.
   */
  @Method()
  async lockSwipeToPrev(lock: boolean) {
    const swiper = await this.getSwiper();
    swiper.allowSlidePrev = !lock;
  }

  /**
   * Lock or unlock the ability to slide to the next or previous slide.
   *
   * @param lock If `true`, disable swiping to the next and previous slide.
   */
  @Method()
  async lockSwipes(lock: boolean) {
    const swiper = await this.getSwiper();
    swiper.allowSlideNext = !lock;
    swiper.allowSlidePrev = !lock;
    swiper.allowTouchMove = !lock;
  }

  /**
   * Get the Swiper instance.
   * Use this to access the full Swiper API.
   * See https://swiperjs.com/swiper-api for all API options.
   */
  @Method()
  async getSwiper(): Promise<any> {
    return this.swiper;
  }

  @Method()
  async stopVideos(): Promise<any> {
    const videos = this.el.querySelectorAll<HTMLVideoElement>('video');
    const iframes = this.el.querySelectorAll<HTMLIFrameElement>('iframe');

    videos.forEach(x => x.pause());
    iframes.forEach(x => x.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'));
  }

  private async initSwiper() {
    const finalOptions = this.normalizeOptions();


    // init swiper core
    // eslint-disable-next-line
    // @ts-ignore
    const { Swiper } = await import('swiper');
    await waitForSlides(this.el);
    const swiper = new Swiper(this.el, finalOptions);
    this.swiperReady = true;
    this.readySwiper(swiper);
  }

  private normalizeOptions(): SwiperOptions {
    // Base options, can be changed
    // TODO Add interface SwiperOptions
    const swiperOptions: SwiperOptions = {
      effect: this.effect,
      direction: 'horizontal',
      initialSlide: 0,
      loop: false,
      parallax: false,
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 500,
      slidesPerGroup: 1,
      centeredSlides: true,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      touchEventsTarget: 'container',
      autoplay: this.autoplay ? {
        delay: this.delay,
        pauseOnMouseEnter: true,
        disableOnInteraction: true
      } : false,
      freeMode: false,
      autoHeight: false,
      setWrapperSize: false,
      zoom: {
        maxRatio: 3,
        minRatio: 1,
        toggle: false,
      },
      preloadImages: false,
      lazy: true,
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      touchStartPreventDefault: false,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 0,
      touchMoveStopPropagation: true,
      touchReleaseOnEdges: false,
      resistance: true,
      resistanceRatio: 0.85,
      watchSlidesProgress: false,
      preventClicks: true,
      preventClicksPropagation: true,
      slideToClickedSlide: false,
      loopAdditionalSlides: 0,
      noSwiping: true,
      runCallbacksOnInit: true,
      coverflowEffect: {
        rotate: 50,
        stretch: 0,
        depth: 100,
        modifier: 1,
        slideShadows: true,
      },
      flipEffect: {
        slideShadows: true,
        limitRotation: true,
      },
      cubeEffect: {
        slideShadows: true,
        shadow: true,
        shadowOffset: 20,
        shadowScale: 0.94,
      },
      fadeEffect: {
        crossFade: true
      },
      a11y: {
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
      },
      navigation: this.navigation,
      modules: [Autoplay, Navigation, Pagination, Scrollbar, CustomEffectFade]
    };

    if (this.pager) {
      swiperOptions.pagination = {
        el: this.pagerEl!,
        type: 'bullets',
        clickable: false,
        hideOnClick: false,
      };
    }

    if (this.scrollbar) {
      swiperOptions.scrollbar = {
        el: this.scrollbarEl!,
        hide: true,
      };
    }

    // Keep the event options separate, we dont want users
    // overwriting these
    const eventOptions: SwiperOptions = {
      on: {
        init: () => {
          setTimeout(() => {
            this.vdsSlidesDidLoad.emit();
            // Forces the swiper instance to update after initializing.
            this.update();
          }, 20);
        },
        slideChangeTransitionStart: (_swiper: Swiper) => this.vdsSlideWillChange.emit,
        slideChangeTransitionEnd: (_swiper: Swiper) => this.vdsSlideDidChange.emit(),
        slideNextTransitionStart: (_swiper: Swiper) => this.vdsSlideNextStart.emit,
        slidePrevTransitionStart: (_swiper: Swiper) => this.vdsSlidePrevStart.emit,
        slideNextTransitionEnd: (_swiper: Swiper) => this.vdsSlideNextEnd.emit,
        slidePrevTransitionEnd: (_swiper: Swiper) => this.vdsSlidePrevEnd.emit,
        transitionStart: (_swiper: Swiper) => this.vdsSlideTransitionStart.emit,
        transitionEnd: (_swiper: Swiper) => this.vdsSlideTransitionEnd.emit,
        sliderMove: (_swiper: Swiper) => this.vdsSlideDrag.emit,
        reachBeginning: (_swiper: Swiper) => this.vdsSlideReachStart.emit,
        reachEnd: (_swiper: Swiper) => this.vdsSlideReachEnd.emit,
        touchStart: (_swiper: Swiper) => this.vdsSlideTouchStart.emit,
        touchEnd: (_swiper: Swiper) => this.vdsSlideTouchEnd.emit,
        tap: (_swiper: Swiper) => this.vdsSlideTap.emit,
        doubleTap: (_swiper: Swiper) => this.vdsSlideDoubleTap.emit,
      },
    };

    const customEvents = !!this.options && !!this.options.on ? this.options.on : {};

    // merge "on" event listeners, while giving our event listeners priority
    const mergedEventOptions = { on: { ...customEvents, ...eventOptions.on } };

    // Merge the base, user options, and events together then pas to swiper
    return { ...swiperOptions, ...this.options, ...mergedEventOptions };
  }

  private renderSlideLeftSVG(): string {
    return (
      <svg class="text-berry" version="1.1" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 14 29" xmlSpace="preserve" width="14" height="29">
        <path d="M13,1L2.5,11.5c-1.7,1.3-1.9,3.7-0.6,5.4c0.2,0.2,0.4,0.4,0.6,0.6L13,28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    );
  }

  private renderSlideRightSVG(): string {
    return (
      <svg class="text-berry" xmlns="http://www.w3.org/2000/svg" data-name="2px_Line_Icons" viewBox="57 49.5 13.98 29" width="13.98" height="29">
        <path d="M58,77.5,68.52,67a3.82,3.82,0,0,0,0-6L58,50.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"></path>
      </svg>
    );
  }

  render() {
    return (
      <Host class="swiper-container">
        <div class="swiper-wrapper" part="swiper-wrapper">
          <slot></slot>
        </div>
        {
          this.pager &&
          <div class="swiper-pagination" ref={(el) => (this.pagerEl = el)}></div>
        }
        {
          this.navigation &&
          <button class="swiper-button-prev"
                  onClick={() => this.slidePrev()}>{this.renderSlideLeftSVG()}</button>
        }
        {
          this.navigation &&
          <button class="swiper-button-next"
                  onClick={() => this.slideNext()}>{this.renderSlideRightSVG()}</button>
        }
        {
          this.scrollbar &&
          <div class="swiper-scrollbar" ref={(el) => (this.scrollbarEl = el)}></div>
        }
      </Host>
    );
  }
}

const waitForSlides = (el: HTMLElement) => {
  return Promise.all(
    Array.from(el.querySelectorAll('vds-slide')).map((s) => new Promise((resolve) => componentOnReady(s, resolve)))
  );
};
